import React, { useEffect, useMemo, useRef, useState } from "react";
import Others from "./Others";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import {
  addFavourite,
  removeFavourite,
} from "../../Components/redux/favouritesSlice";
import { addCartItem } from "../../Components/redux/cartItemsSlice";
import { toast } from "react-toastify";
import { person } from "../../assets/images";
import { addComment } from "../../Components/redux/commentsSlice";

export default function ProductDetail() {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
   const navigate = useNavigate();
  const [list, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalIndex, setActiveModalIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [message, setMessage] = useState("");
  const swiperRef = useRef(null);
  const dispatch = useDispatch();
  const favourites = useSelector((state) => state.favourites.items);
  const [product, setProduct] = useState(null);
  const comments = useSelector((state) => state.comments.items);
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")} ${String(
    now.getHours()
  ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(
    now.getSeconds()
  ).padStart(2, "0")}`;

  const user = useSelector((state) => state.user);

  const isLoggedInUser = user.name;
  const isLoggedInUserSurname = user.surname;
  const userId = user.id;

  useEffect(() => {
    const getProduct = async () => {
      try {
        const res = await axios.get(
          `https://www.elzabeauty.az/elza/dashboard.php`
        );
        const filtered = res.data.data.find((p) => p.id == id);
        setProduct(filtered);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    getProduct();
  }, [id]);

  const filteredComments = useMemo(() => {
    return comments.filter((comment) => comment.product_id == id);
  }, [comments, id]);

  const [showForm, setShowForm] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!newComment || !userId) {
      toast.error(t("Zəhmət olmasa rəyinizi daxil edin."));
      return;
    }

    setLoading(true);
    try {
      dispatch(
        addComment({
          content: newComment,
          product_id: product.id,
          user_id: userId,
          username: isLoggedInUser + " " + isLoggedInUserSurname,
          created_at: today,
        })
      );

      const formData = new FormData();
      formData.append("user_id", userId);
      formData.append("product_id", id);
      formData.append("content", newComment);

      const res = await fetch("https://www.elzabeauty.az/elza/comments.php", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (data.status === 1) {
        toast.success(t("Rəyiniz uğurla əlavə edildi"));
        setNewComment("");
        setShowForm(false);
        getProducts(); // Yorumları yeniden al
        // opsiyonel: yorumları yeniden yüklemek için props’tan gelen bir callback varsa onu çağırabilirsin
      } else {
        toast.error(t("Xəta baş verdi"));
      }
    } catch (error) {
      alert("Xəta: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const isFavourite = useMemo(() => {
    if (!product || !favourites) return false;
    return favourites.includes(Number(product.id));
  }, [product, favourites]);

  // Favori ekle/sil fonksiyonu - optimistik UI
  const toggleFavourite = async () => {
    // Kullanıcı giriş yapmamışsa uyarı göster ve devam etme
    if (!userId) {
      navigate(`/${currentLanguage}/login`);
      toast.error(t("Zəhmət olmasa giriş edin"));
      return; 
    }

    if (!product?.id) return;

    try {
      if (isFavourite) {
        dispatch(removeFavourite(product.id));
        await axios.delete("https://www.elzabeauty.az/elza/favourites.php", {
          data: new URLSearchParams({
            user_id: userId,
            product_id: product.id,
          }),
        });
        setMessage(t("Favorilerden çıkarıldı"));
        toast.error(t("Sevimlilərdən çıxarıldı"));
      } else {
        dispatch(addFavourite(product.id));
        await axios.post(
          "https://www.elzabeauty.az/elza/favourites.php",
          new URLSearchParams({ user_id: userId, product_id: product.id })
        );
        setMessage(t("Favorilere eklendi"));
        toast.success(t("Sevimlilərə əlavə olundu"));
      }
    } catch (error) {
      setMessage(t("Favori güncelleme başarısız"));
      console.error("Favori güncelleme hatası:", error);
      // Hata durumunda state'i eski haline döndürmek için ek mantık ekleyebilirsin
    }
  };

  const addToCart = async () => {
    // Kullanıcı giriş yapmamışsa uyarı göster ve devam etme
    if (!userId) {
      navigate(`/${currentLanguage}/login`);
      toast.error(t("Zəhmət olmasa giriş edin"));
      return;
    }

    // Ürün bilgisi yoksa işlemi durdur
    if (!product?.id) return;

    // Ürün stokta mı kontrol et
    if (product.isExist !== 1) {
      toast.error(t("Məhsul stokda yoxdur"));
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append("user_id", userId);
      params.append("product_id", product.id);
      params.append("quantity", 1);

      const res = await axios.post(
        "https://www.elzabeauty.az/elza/cartItems.php",
        params
      );

      // Redux'a da ekle
      dispatch(
        addCartItem({
          user_id: userId,
          product_id: product.id,
          quantity: 1,
        })
      );

      setMessage(res.data.message || t("Sepete eklendi."));
      toast.success(
  <div onClick={() => navigate(`/${currentLanguage}/cart`)} style={{ cursor: "pointer" }}>
    {t("Səbətə əlavə olundu")}
  </div>
      )  
    } catch (error) {
      console.error("Sepete ekleme hatası:", error);
      setMessage(t("Sepete ekleme başarısız.")); 
      toast.error(t("Səbətə əlavə edilə bilmədi"));
    }
  };

  const getImagesArray = (item) => {
    return [
      item.image1,
      item.image2,
      item.image3,
      item.image4,
      item.image5,
    ].filter(Boolean);
  };

  const images = getImagesArray(list);

  const openModal = (index) => {
    setActiveModalIndex(index);
    setIsModalOpen(true);
  };

  const changeImage = (direction) => {
    const newIndex =
      (activeModalIndex + direction + images.length) % images.length;
    setActiveModalIndex(newIndex);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    getProducts();
  }, []);

  const getProducts = async () => {
    try {
      const response = await axios.get(
        `https://www.elzabeauty.az/elza/dashboard.php`,
        { withCredentials: false }
      );
      const filteredProducts = response.data.data.filter(
        (product) => product.id == id
      );
      setItems(filteredProducts[0]);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const getLocalizedField = (item, fieldBase) => {
    if (currentLanguage === "en")
      return item[`${fieldBase}En`] || item[fieldBase];
    if (currentLanguage === "ru")
      return item[`${fieldBase}Ru`] || item[fieldBase];
    return item[fieldBase];
  };

  if (isLoading) return <div>{t("Yüklənir...")}</div>;
  if (!product) return <div>{t("Məhsul tapılmadı.")}</div>;

  return (
    <>
      <Helmet>
        <title>{getLocalizedField(list, "meta_tag_title")}</title>
        <meta
          name="description"
          content={getLocalizedField(list, "meta_tag_description")}
        />
        <meta
          name="keywords"
          content={getLocalizedField(list, "meta_tag_keywords")}
        />
      </Helmet>

      <div>
        <div className="md:max-w-container max-w-containerSm mx-auto pb-[88px] md:pb-[112px]">
          <div className="md:flex justify-between mt-[40px] md:mt-[88px]">
            {/* LEFT - IMAGE SECTION */}
            <div className="md:w-[475px]">
              <div className="bg-white mb-3 flex justify-center items-center md:w-[475px] h-[411px] rounded border border-[#e2e2e2]">
                <Swiper
                  ref={swiperRef}
                  spaceBetween={30}
                  centeredSlides={true}
                  autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                  }}
                  pagination={{ clickable: true }}
                  navigation={true}
                  onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                  modules={[Autoplay, Pagination, Navigation]}
                  className="main-slider"
                >
                  {images.map((image, index) => (
                    <SwiperSlide key={index}>
                      <div className="relative bg-white w-full h-[354px] rounded-xl flex items-center justify-center cursor-pointer">
                        <img
                          className="max-w-full max-h-full object-contain"
                          src={`https://www.elzabeauty.az/elza/${image}`}
                          alt={`Slide ${index + 1}`}
                          onClick={() => openModal(index)}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              {/* THUMBNAILS */}
              <div className="flex justify-center mt-5 space-x-3">
                {images.map((slide, index) => (
                  <div
                    key={index}
                    className={`cursor-pointer border-2 ${
                      index === activeIndex
                        ? "border-[#264D92]"
                        : "border-transparent"
                    } rounded-lg bg-white w-[80px] h-[60px] flex items-center justify-center`}
                    onClick={() => swiperRef.current.swiper.slideTo(index)}
                  >
                    <img
                      className="max-w-full max-h-full object-contain"
                      src={`https://www.elzabeauty.az/elza/${slide}`}
                      alt={`Thumbnail ${index + 1}`}
                    />
                  </div>
                ))}
              </div>

              {/* MODAL */}
              {isModalOpen && (
                <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-75 flex items-center justify-center z-50">
                  <div className="relative">
                    <button
                      onClick={closeModal}
                      className="absolute top-2 right-2 text-[#264D92] text-4xl font-bold z-20"
                    >
                      ×
                    </button>
                    <div className="bg-white md:w-[500px] md:h-[500px] rounded-lg flex items-center justify-center relative">
                      <button
                        className="absolute left-0 text-white text-4xl font-bold bg-black bg-opacity-50 px-3 py-2 z-10"
                        onClick={() => changeImage(-1)}
                      >
                        &#60;
                      </button>
                      {images[activeModalIndex] && (
                        <img
                          className="max-w-full max-h-full object-contain"
                          src={`https://www.elzabeauty.az/elza/${images[activeModalIndex]}`}
                          alt="Selected"
                        />
                      )}
                      <button
                        className="absolute right-0 text-white text-4xl font-bold bg-black bg-opacity-50 px-3 py-2 z-10"
                        onClick={() => changeImage(1)}
                      >
                        &#62;
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT - DETAILS SECTION */}
            <div className="md:w-[761px] mt-[65px] md:mt-0 text-[#121212]">
              <div className="md:flex justify-between items-center">
                <h4 className="text-[24px] md:text-[28px] font-medium mb-4 md:mb-0">
                  {getLocalizedField(list, "name")}
                </h4>
              </div>
              <div className="flex gap-2 mt-2">
                {list.colors?.map((colorItem, index) => (
                  <div
                    key={index}
                    className="w-7 h-7 flex items-center justify-center rounded-full border border-[#838383] bg-white"
                    title={getLocalizedField(colorItem, "name")}
                  >
                    <div
                      className="w-5 h-5 rounded-full"
                      style={{ backgroundColor: colorItem.code }}
                    ></div>
                  </div>
                ))}
              </div>
              <div className="md:flex justify-between items-center">
                {getLocalizedField(list, "secondPrice") ? (
                  <h4 className="text-[24px] md:text-[28px] font-medium mt-5">
                    <span className="line-through text-[#697586] mr-2">
                      {getLocalizedField(list, "firstPrice")} ₼
                    </span>
                    <span className="text-firstColor">
                      {getLocalizedField(list, "secondPrice")} ₼
                    </span>
                  </h4>
                ) : (
                  <h4 className="text-[24px] md:text-[28px] font-medium mt-5">
                    {getLocalizedField(list, "firstPrice")} ₼
                  </h4>
                )}
              </div>
              <p
                className={`mt-2 font-semibold ${
                  product?.isExist === 1 ? "text-green-600" : "text-red-600"
                }`}
              >
                {product?.isExist === 1 ? "Stokda var" : "Stokda yoxdur"}
              </p>

              <p
                className="mb-9 text-sm md:text-[16px] mt-4 leading-6"
                dangerouslySetInnerHTML={{
                  __html: getLocalizedField(list, "information"),
                }}
              />
              <p className="font-medium mt-8 text-[#383838]">{t("Məlumat")}</p>
              <p
                className="mb-9 text-sm md:text-[16px] mt-4 leading-6"
                dangerouslySetInnerHTML={{
                  __html: getLocalizedField(list, "description"),
                }}
              />
              <div className="flex flex-col md:flex-row mt-5 gap-3">
                <button
                  onClick={addToCart}
                  className="h-[52px] w-full md:w-[312px] bg-[#181D27] rounded-[8px] text-white transition"
                >
                  {t("Səbətə Əlavə Et")}
                </button>

                <button
                  onClick={toggleFavourite}
                  className="h-[52px] w-full md:w-[312px] bg-[#F4F4F4] rounded-[8px] text-[#181D27] border border-[#181D27] flex items-center justify-center gap-2 text-[20px]"
                  aria-label={
                    isFavourite ? t("Favorilerden çıkar") : t("Favorilere ekle")
                  }
                >
                  <span>{isFavourite ? "❤️" : "🤍"}</span>
                  <span>{t("Bəyən")}</span>
                </button>
              </div>
              {userId && (
                <div className="mt-10 md:mt-20 text-[#222]">
                  <p className="text-[#222] text-[28px] md:text-[40px] font-bold text-center">
                    {t("Rəylər")}
                  </p>

                  <div className=" mt-4">
                    <button
                      onClick={() => setShowForm(!showForm)}
                      className="bg-[#E497BB] text-white px-4 py-2 rounded-md"
                    >
                      {t("Yeni Rəy Yaz")}
                    </button>
                  </div>

                  {showForm && (
                    <div className="max-w-xl mx-auto mt-4 bg-white p-4 rounded-xl shadow">
                      <textarea
                        placeholder={t("Rəyinizi yazın...")}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        className="w-full h-24 p-2 border border-gray-300 rounded resize-none"
                      />
                      <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="mt-3 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                      >
                        {loading ? t("Göndərilir...") : t("Göndər")}
                      </button>
                    </div>
                  )}

                  <div className="mt-[24px] md:mt-10">
                    {filteredComments && filteredComments.length > 0 ? (
                      <div>
                        {filteredComments?.map((comment, index) => (
                          <div
                            key={index}
                            className="mb-6 rounded-xl p-4 flex border border-[#CFCFCF]"
                          >
                            <img
                              className="w-[38px] h-[38px] mr-6"
                              src={person}
                              alt="User Avatar"
                            />
                            <div className="flex flex-col">
                              <div className="font-semibold">
                                {comment.username}
                              </div>
                              <div className="text-sm text-gray-600">
                                {comment.created_at}
                              </div>
                            </div>
                            <div className="mx-6 bg-[#9CA3AF] h-[45px] w-[1px]"></div>
                            <div className="flex flex-col ml-6">
                              <div className="mt-[2px]">{comment.content}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 text-[16px]">
                        {t("İlk rəyi yazın")}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {/* {message && (
                <p className="mt-4 text-center text-red-600 font-semibold">
                  {message}
                </p>
              )} */}
            </div>
          </div>
          <Others />
        </div>
      </div>
    </>
  );
}
